import React, { useCallback, useEffect, useRef } from "react";
import { createContext, useContext, useState } from "react";
import axios from "axios";
import { getToken } from "../auth";
import { getDefaultValues } from "../services/app.service";
import { PERSONA_INITIAL_OBJECT } from "../utils/settings";
import { generateChatSessionId } from "../utils/chat";

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider = ({ children, emailData, mode, isDarkMode, isMobile }) => {
  const [selectedModel, setSelectedModel] = useState("");
  const [conversation, setConversation] = useState<Message[]>([]);
  const [isChatActive, setIsChatActive] = useState(false);
  const [selectedAction, setSelectedAction] = useState("");
  const [prompt, setPrompt] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("");
  const [selectedBrand, setSelectedBrand] = useState<Brand>({ id: "", name: "" });
  const [selectedPersona, setSelectedPersona] = useState<Persona>(PERSONA_INITIAL_OBJECT);
  const [selectedTovId, setSelectedTovId] = useState("");
  const [modelProvider, setModelProvider] = useState("");
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [cancelStreamRequested, setCancelStreamRequested] = useState(false);
  const [token, setToken] = useState("");
  const [tokenExpirationTime, setTokenExpirationTime] = useState<number | null>(null);
  // const tokenRefreshInterval = 10 * 60 * 1000; // 10 minutes
  const [chatSessionId, setChatSessionId] = useState(generateChatSessionId());
  const [isStreamingRequested, setIsStreamingRequested] = useState(false);
  const [isDefaultValuesLoading, setIsDefaultValuesLoading] = useState(true);

  const scopes = [`${process.env.REACT_APP_SCOPE}/.default`];

  const tokenRefreshInterval = 50 * 60 * 1000; // 50 minutes

  const refreshTokenRef = useRef<() => Promise<void>>();

  const refreshToken = useCallback(async () => {
    try {
      console.log("Acquiring new token...");
      const newToken = await getToken(scopes);
      axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;

      const expirationTime = Date.now() + tokenRefreshInterval;

      setToken(newToken);
      setTokenExpirationTime(expirationTime);

      console.log("Token refreshed successfully");
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      console.error("Error refreshing token:", err);
    }
  }, [scopes]);

  // Update ref when refreshToken changes
  refreshTokenRef.current = refreshToken;

  // Effect runs only once
  useEffect(() => {
    console.log("Setting up token refresh");

    // Initial fetch
    refreshTokenRef.current?.();

    const intervalId = setInterval(() => {
      console.log("Periodic refresh");
      refreshTokenRef.current?.();
    }, tokenRefreshInterval);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (token?.length > 0) {
      const defaultData = async () => {
        const response = await getDefaultValues();
        setSelectedModel(response.name_of_model);
        setSelectedLanguage(response.response_language);
        setSelectedBrand(response.brand);
        setSelectedDivision(response.division);
        setSelectedPersona(response.persona ?? PERSONA_INITIAL_OBJECT);
        setSelectedTovId(response.tov_id);
        setModelProvider(response.provider);
        setIsDefaultValuesLoading(false);
      };
      defaultData();
    }
  }, [token]);

  return (
    <AppContext.Provider
      value={{
        isDarkMode,
        selectedModel,
        modelProvider,
        selectedLanguage,
        token,
        conversation,
        isChatActive,
        selectedAction,
        setConversation,
        setIsChatActive,
        prompt,
        emailData,
        cancelStreamRequested,
        chatSessionId,
        setSelectedAction,
        setPrompt,
        selectedDivision,
        selectedBrand,
        selectedPersona,
        selectedTovId,
        mode,
        isMobile,
        isStreamingRequested,
        isDefaultValuesLoading,
        setSelectedBrand,
        setSelectedDivision,
        setSelectedPersona,
        setSelectedLanguage,
        setSelectedModel,
        setSelectedTovId,
        setModelProvider,
        isChatLoading,
        setIsChatLoading,
        setCancelStreamRequested,
        setIsStreamingRequested,
        setIsDefaultValuesLoading,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error("useAppContext must be used inside AppProvider");
  return context;
};
